import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ShieldCheck, ExternalLink, BookOpen, Info, Search, FileText, Loader2, X,
  Upload, Pencil, Trash2, Download, Lock, Plus,
} from "lucide-react";

const SOC = [
  "Fly only during day and within visual line-of-sight (VLOS)",
  "Do not fly higher than 120 m (400 ft) AGL",
  "Keep at least 30 m away from people not involved in the operation",
  "Fly only one RPA at a time",
  "Do not fly over or above people or populous areas",
  "Do not fly within 5.5 km of a controlled aerodrome",
  "Do not fly in the area of a public safety or emergency operation",
];

const LINKS = [
  { label: "CASR Part 101 — Unmanned aircraft & rockets", url: "https://www.casa.gov.au/rules/regulatory-framework/casr/part-101-casr-unmanned-aircraft-and-rockets" },
  { label: "Part 101 Manual of Standards (MOS) 2019", url: "https://www.legislation.gov.au/Details/F2019L00921" },
  { label: "CASA Drones — Rules & safety", url: "https://www.casa.gov.au/drones" },
  { label: "Register your drone / operator accreditation", url: "https://www.casa.gov.au/drones/register" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const IMAGE_EXT = ["jpg", "jpeg", "png", "webp", "gif"];

function docUrl(d) {
  if (!d) return null;
  if (d.is_builtin && d.pdf) return d.pdf;
  if (d.storage_path) {
    const token = localStorage.getItem("session_token");
    const u = `${BACKEND}/api/files/${d.storage_path}`;
    return token ? `${u}?auth=${encodeURIComponent(token)}` : u;
  }
  return null;
}

const emptyForm = { title: "", version: "", published_date: "", file: null };

export default function CasaPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [docs, setDocs] = useState([]);
  const [activeDoc, setActiveDoc] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // doc being edited, or null for new
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    api.get("/docs").then((r) => setDocs(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const runSearch = async () => {
    if (!query.trim() || !activeDoc) return;
    setSearching(true); setResults(null);
    try {
      const r = await api.get(`/docs/${activeDoc.id}/search`, { params: { q: query } });
      setResults(r.data);
    } catch { setResults({ count: 0, results: [] }); }
    finally { setSearching(false); }
  };

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({ title: d.title || "", version: d.version || "", published_date: d.published_date || "", file: null });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!editing && !form.file) { toast.error("Please choose a file to upload"); return; }
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("version", form.version);
    fd.append("published_date", form.published_date);
    if (form.file) fd.append("file", form.file);
    setBusy(true);
    try {
      if (editing) {
        const r = await api.put(`/docs/${editing.id}`, fd);
        toast.success("Document updated");
        if (activeDoc?.id === editing.id) setActiveDoc(r.data);
      } else {
        await api.post("/docs", fd);
        toast.success("Document uploaded");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    const d = deleteTarget;
    setDeleteTarget(null);
    try {
      await api.delete(`/docs/${d.id}`);
      toast.success("Document deleted");
      if (activeDoc?.id === d.id) { setActiveDoc(null); setResults(null); }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const viewUrl = docUrl(activeDoc);
  const activeExt = (activeDoc?.ext || "").toLowerCase();
  const isImage = IMAGE_EXT.includes(activeExt);
  const isPdf = activeExt === "pdf";
  const isViewable = isPdf || isImage;

  return (
    <div className="space-y-6 fade-up">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Regulatory Compliance</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading flex items-center gap-2">
            <ShieldCheck className="h-8 w-8 text-lime-500" /> CASA
          </h1>
          <p className="text-slate-500 mt-1">CASR Part 101 &amp; MOS — read the source documents and search them instantly.</p>
        </div>
        {isAdmin && (
          <Button data-testid="doc-upload-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white">
            <Upload className="h-4 w-4 mr-1.5" /> Upload document
          </Button>
        )}
      </div>

      {/* Document reader + search */}
      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {docs.map((d) => (
            <button key={d.id} data-testid={`doc-tab-${d.id}`} onClick={() => { setActiveDoc(d); setResults(null); }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all inline-flex items-center gap-1.5 ${activeDoc?.id === d.id ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              <FileText className="h-4 w-4" /> {d.title}
              {d.version ? <span className="text-xs opacity-70">v{d.version}</span> : null}
              {d.pages ? <span className="text-xs opacity-60">({d.pages}p)</span> : null}
            </button>
          ))}
          {docs.length === 0 && <span className="text-sm text-slate-400">No documents yet.</span>}
        </div>

        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="doc-search-input" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder={`Search ${activeDoc?.title || "document"}…`} className="pl-9" />
          </div>
          <Button data-testid="doc-search-btn" onClick={runSearch} className="bg-lime-400 hover:bg-lime-300 text-slate-900">Search</Button>
        </div>

        {searching && <div className="flex items-center gap-2 text-sm text-slate-500 mb-3"><Loader2 className="h-4 w-4 animate-spin" /> Searching…</div>}
        {results && (
          <div className="mb-4 border border-slate-200 rounded-lg" data-testid="doc-search-results">
            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-100">
              <span className="text-sm font-medium text-slate-700">{results.count} match{results.count !== 1 ? "es" : ""} for "{query}"</span>
              <button onClick={() => setResults(null)}><X className="h-4 w-4 text-slate-400" /></button>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
              {results.results.map((r, i) => (
                <div key={`${r.page}-${i}`} className="px-3 py-2.5 hover:bg-lime-50/50">
                  <span className="text-xs font-bold text-lime-700">Page {r.page}</span>
                  <p className="text-sm text-slate-600 mt-0.5">{r.snippet}</p>
                </div>
              ))}
              {results.count === 0 && <p className="px-3 py-4 text-sm text-slate-400">No matches found.</p>}
            </div>
          </div>
        )}

        {activeDoc ? (
          <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-100" data-testid="doc-viewer">
            <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200">
              <span className="text-sm font-medium text-slate-700 truncate">
                {activeDoc.title}
                {activeDoc.published_date ? <span className="ml-2 text-xs text-slate-400">Published {activeDoc.published_date}</span> : null}
              </span>
              <button onClick={() => { setActiveDoc(null); setResults(null); }} data-testid="doc-close-btn" className="text-slate-400 hover:text-slate-900 inline-flex items-center gap-1 text-xs"><X className="h-4 w-4" /> Close</button>
            </div>
            {isPdf && <iframe title={activeDoc.title} src={`${viewUrl}#view=FitH`} className="w-full" style={{ height: "70vh" }} />}
            {isImage && <div className="bg-white flex justify-center p-4"><img src={viewUrl} alt={activeDoc.title} className="max-h-[70vh] object-contain" /></div>}
            {!isViewable && (
              <div className="bg-white py-16 text-center">
                <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500 mb-4">{activeDoc.original_filename || "This file"} can't be previewed here.</p>
                <a href={viewUrl} target="_blank" rel="noreferrer" download>
                  <Button data-testid="doc-download-btn" className="bg-lime-400 hover:bg-lime-300 text-slate-900"><Download className="h-4 w-4 mr-1.5" /> Download</Button>
                </a>
              </div>
            )}
            {isViewable && (
              <div className="px-4 py-2 text-center text-xs text-slate-400 border-t border-slate-200">
                Trouble viewing? <a href={viewUrl} target="_blank" rel="noreferrer" className="text-lime-600 underline">Open in a new tab</a>.
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 py-16 text-center" data-testid="doc-placeholder">
            <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Select a document above to open and read it.</p>
            <p className="text-xs text-slate-400 mt-1">Files load only when you choose one.</p>
          </div>
        )}
      </section>

      {/* Admin: manage documents */}
      {isAdmin && (
        <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="doc-manage-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold font-heading text-slate-900 flex items-center gap-2"><FileText className="h-5 w-5 text-lime-500" /> Manage documents</h2>
            <Button size="sm" variant="outline" onClick={openNew} data-testid="doc-manage-add-btn" className="border-dashed"><Plus className="h-4 w-4 mr-1.5" /> Add</Button>
          </div>
          <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
            {docs.map((d) => (
              <div key={d.id} data-testid={`doc-manage-row-${d.id}`} className="flex items-center gap-3 px-3 py-2.5">
                <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-800 truncate">{d.title}</span>
                    {d.is_builtin && <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400 inline-flex items-center gap-0.5"><Lock className="h-3 w-3" /> built-in</span>}
                  </div>
                  <p className="text-xs text-slate-400 truncate">
                    {[d.version && `v${d.version}`, d.published_date && `Published ${d.published_date}`, d.original_filename, `.${d.ext}`].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <Button size="icon" variant="ghost" onClick={() => openEdit(d)} data-testid={`doc-edit-${d.id}`} className="text-slate-500 hover:text-slate-900 shrink-0"><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(d)} data-testid={`doc-delete-${d.id}`} className="text-red-600 hover:bg-red-50 shrink-0"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            {docs.length === 0 && <p className="px-3 py-4 text-sm text-slate-400">No documents. Upload one to get started.</p>}
          </div>
          <p className="text-xs text-slate-400 mt-3">Allowed: PDF, Word (.docx), Excel (.xlsx), images. PDF / Word / Excel are indexed for search.</p>
        </section>
      )}

      <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 flex gap-3">
        <Info className="h-5 w-5 text-sky-600 shrink-0 mt-0.5" />
        <p className="text-sm text-slate-700">The documents above are indexed for the AI assistant. CASA's live drone-safety API onboarding is currently paused (awaiting Airservices FIMS); when it reopens this section can connect for live airspace advisories.</p>
      </div>

      <section className="bg-white border border-slate-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold font-heading text-slate-900 mb-4 flex items-center gap-2"><BookOpen className="h-5 w-5 text-lime-500" /> Standard Operating Conditions</h2>
        <ul className="space-y-2">
          {SOC.map((s, i) => (
            <li key={s} className="flex items-start gap-2.5 text-sm text-slate-700">
              <span className="h-5 w-5 rounded-full bg-lime-100 text-lime-700 text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">{i + 1}</span>{s}
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold font-heading text-slate-900 mb-4">Official CASA resources</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {LINKS.map((l) => (
            <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" data-testid="casa-link"
              className="flex items-center justify-between gap-2 p-3 rounded-lg border border-slate-200 hover:border-lime-300 hover:bg-lime-50/50 transition-all text-sm text-slate-700">
              <span>{l.label}</span><ExternalLink className="h-4 w-4 text-slate-400 shrink-0" />
            </a>
          ))}
        </div>
      </section>
      <p className="text-xs text-slate-400">Reference only — always confirm current requirements on casa.gov.au.</p>

      {/* Upload / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="doc-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit document" : "Upload document"}</DialogTitle>
            <DialogDescription>
              {editing ? "Update details, or choose a new file to replace the current one." : "PDF, Word (.docx), Excel (.xlsx) or an image. PDF/Word/Excel are indexed for search."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="doc-title">Title *</Label>
              <Input id="doc-title" data-testid="doc-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Operations Manual" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="doc-version">Version</Label>
                <Input id="doc-version" data-testid="doc-version-input" value={form.version} onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))} placeholder="e.g. 2.1" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="doc-date">Published date</Label>
                <Input id="doc-date" type="date" data-testid="doc-date-input" value={form.published_date} onChange={(e) => setForm((f) => ({ ...f, published_date: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="doc-file">{editing ? "Replace file (optional)" : "File *"}</Label>
              <Input id="doc-file" type="file" data-testid="doc-file-input"
                accept=".pdf,.docx,.doc,.xlsx,.xls,.jpg,.jpeg,.png,.webp,.gif"
                onChange={(e) => setForm((f) => ({ ...f, file: e.target.files?.[0] || null }))} />
              {editing && <p className="text-xs text-slate-400">Current: {editing.original_filename || "—"}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="doc-save-btn" className="bg-slate-900 hover:bg-slate-800 text-white">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
              {editing ? "Save changes" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="doc-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>This removes the document from the CASA page and search index. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="doc-delete-confirm" className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
